import { Injectable, inject } from '@angular/core';
import { Map as MapLibreMap } from 'maplibre-gl';
import {
  HandleKpiRasterClickParams,
  KpiRasterAction,
  KpiRasterClickPayload,
  KpiRasterLayerInfo,
  KpiRasterPerMap,
  KpiRasterState,
  KpiRasterTarget,
} from './kpi-raster.types';
import { TColormap } from '../../../types/explorer/sjmap2/colormap/TColormap';
import { TitilerService } from '../../titiler.service';

@Injectable({ providedIn: 'root' })
export class KpiRasterService {
  private titilerService: TitilerService = inject(TitilerService);

  private kpiDefaultColormap: TColormap | null = null;

  private kpiRasterBbox: [number, number, number, number] | null = null;

  private activeKpiRasterLayers: KpiRasterState = {};

  // Configura stile e bbox per i raster KPI (wrapper di setKpiRasterStyle).
  setKpiRasterStyle(
    colormap: TColormap | null,
    bounds?: { type: 'Polygon'; coordinates: number[][][] } | null
  ): void {
    if (colormap && colormap.type === 'named') {
      this.kpiDefaultColormap = {
        ...colormap,
        min: 0,
        max: 1,
      };
    } else {
      this.kpiDefaultColormap = colormap ?? null;
    }

    this.kpiRasterBbox = bounds ? this.computeBboxFromBounds(bounds) : null;
  }

  // Entry point usato dal explorer map service per gestire il click.
  handleKpiRasterClick(params: HandleKpiRasterClickParams): void {
    if (!this.kpiDefaultColormap) return;

    const { payload, sourceKpiType, mainMap, splitMap, isSplitOpen } = params;

    const rawTarget: KpiRasterTarget =
      payload.target === 'split' ? 'split' : 'main';

    const action: KpiRasterAction =
      payload.action === 'MOVE_TARGET' ? 'MOVE_TARGET' : 'TOGGLE';

    const target: KpiRasterTarget =
      rawTarget === 'split' && isSplitOpen && splitMap ? 'split' : 'main';

    const kpiKey = sourceKpiType ?? 'raster';
    const stateForKpi = this.ensureKpiEntry(kpiKey);

    const scenarioKey = this.buildScenarioKey(payload);

    if (action === 'MOVE_TARGET') {
      this.moveScenarioToTarget({
        kpiKey,
        scenarioKey,
        target,
        payload,
        mainMap,
        splitMap,
      });
    } else {
      this.toggleScenarioOnTarget({
        kpiKey,
        scenarioKey,
        target,
        payload,
        mainMap,
        splitMap,
        isSplitOpen,
      });
    }
  }

  /**
   * Chiamata quando chiudiamo la split map.
   * Rimuove SOLO i layer KPI dalla split, mantenendo pulito lo stato.
   */
  clearAllKpiRastersOnSplitClose(splitMap: MapLibreMap | null): void {
    Object.values(this.activeKpiRasterLayers).forEach((entry) => {
      if (!entry.split) return;

      if (splitMap) {
        Object.values(entry.split).forEach((info) => {
          if (!info) return;
          this.removeLayerAndSource(splitMap, info);
        });
      }

      entry.split = {};
    });
  }

  // PRIVATE HELPERS – SOLO logica

  private ensureKpiEntry(kpiKey: string): KpiRasterPerMap {
    if (!this.activeKpiRasterLayers[kpiKey]) {
      this.activeKpiRasterLayers[kpiKey] = {
        main: {},
        split: {},
      };
    }
    const entry = this.activeKpiRasterLayers[kpiKey];
    if (!entry.main) entry.main = {};
    if (!entry.split) entry.split = {};
    return entry;
  }

  private buildScenarioKey(payload: KpiRasterClickPayload): string {
    return `${payload.scenario.type}::${payload.key}`;
  }

  private computeBboxFromBounds(bounds: {
    type: 'Polygon';
    coordinates: number[][][];
  }): [number, number, number, number] | null {
    if (!bounds?.coordinates?.[0]?.length) return null;

    const coords = bounds.coordinates[0];

    return coords.reduce(
      (acc, coord) => [
        Math.min(acc[0], coord[0]), // minLon
        Math.min(acc[1], coord[1]), // minLat
        Math.max(acc[2], coord[0]), // maxLon
        Math.max(acc[3], coord[1]), // maxLat
      ],
      [Infinity, Infinity, -Infinity, -Infinity] as [
        number,
        number,
        number,
        number
      ]
    ) as [number, number, number, number];
  }

  private buildIds(
    kpiKey: string,
    scenarioType: string,
    key: string,
    target: KpiRasterTarget
  ): { sourceId: string; layerId: string } {
    const sourceId = `kpi-${kpiKey}-${scenarioType}-${key}-src-${target}`;
    const layerId = `kpi-${kpiKey}-${scenarioType}-${key}-lyr-${target}`;
    return { sourceId, layerId };
  }

  private createOrReplaceRasterLayer(
    map: MapLibreMap,
    info: {
      sourceId: string;
      layerId: string;
      tilesUrl: string;
    }
  ) {
    const { sourceId, layerId, tilesUrl } = info;

    if (map.getLayer(layerId)) {
      map.removeLayer(layerId);
    }
    if (map.getSource(sourceId)) {
      map.removeSource(sourceId);
    }

    const sourceSpec: any = {
      type: 'raster',
      tiles: [tilesUrl],
      tileSize: 256,
    };

    if (this.kpiRasterBbox) {
      sourceSpec.bounds = this.kpiRasterBbox;
    }

    map.addSource(sourceId, sourceSpec);
    map.addLayer({
      id: layerId,
      type: 'raster',
      source: sourceId,
      paint: { 'raster-opacity': 0.9 },
    });
  }

  private removeLayerAndSource(map: MapLibreMap, info: KpiRasterLayerInfo) {
    if (map.getLayer(info.layerId)) {
      map.removeLayer(info.layerId);
    }
    if (map.getSource(info.sourceId)) {
      map.removeSource(info.sourceId);
    }
  }

  // MOVE_TARGET
  private moveScenarioToTarget(args: {
    kpiKey: string;
    scenarioKey: string;
    target: KpiRasterTarget;
    payload: KpiRasterClickPayload;
    mainMap: MapLibreMap | null;
    splitMap: MapLibreMap | null;
  }): void {
    const { kpiKey, scenarioKey, target, payload, mainMap, splitMap } = args;
    if (!this.kpiDefaultColormap) return;

    const stateForKpi = this.ensureKpiEntry(kpiKey);

    const fromMain = stateForKpi.main[scenarioKey];
    const fromSplit = stateForKpi.split[scenarioKey];

    if (fromMain && mainMap) {
      this.removeLayerAndSource(mainMap, fromMain);
      delete stateForKpi.main[scenarioKey];
    }

    if (fromSplit && splitMap) {
      this.removeLayerAndSource(splitMap, fromSplit);
      delete stateForKpi.split[scenarioKey];
    }

    const moveMap = target === 'main' ? mainMap : splitMap;
    if (!moveMap) return;

    const tilesUrl = this.titilerService.getTilesURL(
      payload.uri,
      this.kpiDefaultColormap as TColormap
    );

    const { sourceId, layerId } = this.buildIds(
      kpiKey,
      payload.scenario.type,
      payload.key,
      target
    );

    this.createOrReplaceRasterLayer(moveMap, { sourceId, layerId, tilesUrl });

    const layerInfo: KpiRasterLayerInfo = {
      layerId,
      sourceId,
      scenarioType: payload.scenario.type,
      key: payload.key,
    };

    if (target === 'main') {
      stateForKpi.main[scenarioKey] = layerInfo;
    } else {
      stateForKpi.split[scenarioKey] = layerInfo;
    }
  }

  // TOGGLE
  private toggleScenarioOnTarget(args: {
    kpiKey: string;
    scenarioKey: string;
    target: KpiRasterTarget;
    payload: KpiRasterClickPayload;
    mainMap: MapLibreMap | null;
    splitMap: MapLibreMap | null;
    isSplitOpen: boolean;
  }): void {
    const {
      kpiKey,
      scenarioKey,
      target,
      payload,
      mainMap,
      splitMap,
      isSplitOpen,
    } = args;
    if (!this.kpiDefaultColormap) return;

    const map = target === 'main' ? mainMap : splitMap;
    if (!map) return;

    const stateForKpi = this.ensureKpiEntry(kpiKey);
    const entryForTarget =
      target === 'main' ? stateForKpi.main : stateForKpi.split;

    const prev = entryForTarget[scenarioKey];

    // se già attivo su questa mappa: spengo e basta
    if (prev) {
      this.removeLayerAndSource(map, prev);
      delete entryForTarget[scenarioKey];
      return;
    }

    // su mainMap e split chiusa un solo raster KPI alla volta
    if (target === 'main' && !isSplitOpen) {
      Object.values(entryForTarget).forEach((info) => {
        if (!info) return;
        this.removeLayerAndSource(map, info);
      });
      Object.keys(entryForTarget).forEach((k) => delete entryForTarget[k]);
    }

    const tilesUrl = this.titilerService.getTilesURL(
      payload.uri,
      this.kpiDefaultColormap as TColormap
    );

    const { sourceId, layerId } = this.buildIds(
      kpiKey,
      payload.scenario.type,
      payload.key,
      target
    );

    this.createOrReplaceRasterLayer(map, { sourceId, layerId, tilesUrl });

    entryForTarget[scenarioKey] = {
      layerId,
      sourceId,
      scenarioType: payload.scenario.type,
      key: payload.key,
    };
  }
}
